import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import {
  SvgBolt,
  SvgCheveronDown,
  SvgCheveronRight,
  SvgCog,
  SvgCreditCard,
  SvgDotsHorizontalTriple,
  SvgReports,
  SvgStoreFront,
  SvgTag,
  SvgTuning,
  SvgWallet,
} from '@actual-app/components/icons/v1';
import { SvgCalendar3 } from '@actual-app/components/icons/v2';
import { Menu } from '@actual-app/components/menu';
import { Popover } from '@actual-app/components/popover';
import { View } from '@actual-app/components/view';

import { useIsTestEnv } from '#hooks/useIsTestEnv';
import { useNavigate } from '#hooks/useNavigate';
import { useSyncServerStatus } from '#hooks/useSyncServerStatus';

import { Item } from './Item';
import { SecondaryItem } from './SecondaryItem';
import { useSidebar } from './SidebarProvider';

export function PrimaryButtons() {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const onToggle = useCallback(() => setOpen(open => !open), []);
  const location = useLocation();
  const navigate = useNavigate();
  const sidebar = useSidebar();
  const isCollapsed = sidebar?.collapsed ?? false;

  const syncServerStatus = useSyncServerStatus();
  const isTestEnv = useIsTestEnv();
  const isUsingServer = syncServerStatus !== 'no-server' || isTestEnv;

  const isActive = [
    '/payees',
    '/rules',
    '/bank-sync',
    '/jev',
    '/settings',
    '/tools',
  ].some(route => location.pathname.startsWith(route));

  useEffect(() => {
    if (isActive && !isCollapsed) {
      setOpen(true);
    }
  }, [isActive, isCollapsed, location.pathname]);

  const moreMenuItems = [
    { name: '/payees', text: t('Payees'), icon: SvgStoreFront, iconSize: 13 },
    { name: '/rules', text: t('Rules'), icon: SvgTuning, iconSize: 13 },
    ...(isUsingServer
      ? [
          {
            name: '/bank-sync',
            text: t('Bank Sync'),
            icon: SvgCreditCard,
            iconSize: 13,
          },
        ]
      : []),
    ...(isUsingServer
      ? [
          {
            name: '/jev',
            text: t('JEV review'),
            icon: SvgBolt,
            iconSize: 13,
          },
        ]
      : []),
    { name: '/tags', text: t('Tags'), icon: SvgTag, iconSize: 13 },
    { name: '/settings', text: t('Settings'), icon: SvgCog, iconSize: 13 },
  ];

  if (isCollapsed) {
    return (
      <View data-testid="sidebar-primary-buttons" style={{ flexShrink: 0 }}>
        <Item title={t('Budget')} Icon={SvgWallet} to="/budget" />
        <Item title={t('Reports')} Icon={SvgReports} to="/reports" />
        <Item title={t('Schedules')} Icon={SvgCalendar3} to="/schedules" />
        <Item
          buttonRef={moreButtonRef}
          title={t('More')}
          Icon={SvgDotsHorizontalTriple}
          onClick={() => setPopoverOpen(prev => !prev)}
          forceActive={isActive}
        />
        <Popover
          placement="right top"
          offset={8}
          triggerRef={moreButtonRef}
          isOpen={isPopoverOpen}
          onOpenChange={open => setPopoverOpen(open)}
        >
          <Menu
            onMenuSelect={(path: string) => {
              setPopoverOpen(false);
              void navigate(path);
            }}
            items={moreMenuItems}
          />
        </Popover>
      </View>
    );
  }

  return (
    <View data-testid="sidebar-primary-buttons" style={{ flexShrink: 0 }}>
      <Item title={t('Budget')} Icon={SvgWallet} to="/budget" />
      <Item title={t('Reports')} Icon={SvgReports} to="/reports" />
      <Item title={t('Schedules')} Icon={SvgCalendar3} to="/schedules" />
      <Item
        title={t('More')}
        Icon={isOpen ? SvgCheveronDown : SvgCheveronRight}
        onClick={onToggle}
        style={{ marginBottom: isOpen ? 8 : 0 }}
        forceActive={!isOpen && isActive}
      />
      {isOpen && (
        <>
          <SecondaryItem
            title={t('Payees')}
            Icon={SvgStoreFront}
            to="/payees"
            indent={15}
          />
          <SecondaryItem
            title={t('Rules')}
            Icon={SvgTuning}
            to="/rules"
            indent={15}
          />
          {isUsingServer && (
            <SecondaryItem
              title={t('Bank Sync')}
              Icon={SvgCreditCard}
              to="/bank-sync"
              indent={15}
            />
          )}
          {isUsingServer && (
            <SecondaryItem
              title={t('JEV review')}
              Icon={SvgBolt}
              to="/jev"
              indent={15}
            />
          )}
          <SecondaryItem
            title={t('Tags')}
            Icon={SvgTag}
            to="/tags"
            indent={15}
          />
          <SecondaryItem
            title={t('Settings')}
            Icon={SvgCog}
            to="/settings"
            indent={15}
          />
        </>
      )}
    </View>
  );
}
